import React, { useState, useRef, useEffect } from 'react';
import { X, Search, MessageSquare, Pin, Plus } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatDistanceToNow } from 'date-fns';
import chatService from '../../services/chatService';
import { useAuth } from '../../contexts/AuthContext';

export default function ChatSearchModal({
  isOpen,
  onClose,
  chats = [],
  currentChatId,
  onChatSelect,
  onCreateNewChat,
  searchQuery: externalSearchQuery = '',
  onSearchChange
}) {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState(externalSearchQuery);
  const [chatsWithMessages, setChatsWithMessages] = useState(chats);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const inputRef = useRef(null);

  // Load messages for all chats when modal opens (for search)
  useEffect(() => {
    if (isOpen && user) {
      setLoadingMessages(true);
      chatService.getUserChats(user.id, true).then(result => {
        if (result.data) {
          setChatsWithMessages(result.data);
        }
        setLoadingMessages(false);
      }).catch(err => {
        console.error('Error loading chats with messages:', err);
        setChatsWithMessages(chats);
        setLoadingMessages(false);
      });
    } else {
      setChatsWithMessages(chats);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user]);

  // Sync with external search query
  useEffect(() => {
    setSearchQuery(externalSearchQuery);
  }, [externalSearchQuery]);

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Filter chats - show all chats (no tab filtering), search by title and message content
  const getFilteredChats = () => {
    let filtered = chatsWithMessages.filter(chat => {
      if (!searchQuery.trim()) {
        return true; // Show all chats when no search query
      }
      
      const query = searchQuery.toLowerCase();
      
      // Search in title
      if (chat.title?.toLowerCase().includes(query)) {
        return true;
      }
      
      // Search in message content (if messages are loaded)
      if (chat.messages && Array.isArray(chat.messages)) {
        const messageText = chat.messages
          .map(msg => msg.content || '')
          .join(' ')
          .toLowerCase();
        if (messageText.includes(query)) {
          return true;
        }
      }
      
      // Search in folder name
      if (chat.folder?.name?.toLowerCase().includes(query)) {
        return true;
      }
      
      return false;
    });

    // Sort: pinned first, then by updated_at
    return filtered.sort((a, b) => {
      if (a.is_pinned && !b.is_pinned) return -1;
      if (!a.is_pinned && b.is_pinned) return 1;
      return new Date(b.updated_at || 0) - new Date(a.updated_at || 0);
    });
  };

  const filteredChats = getFilteredChats();

  // Group chats by date
  const groupChatsByDate = (chats) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const groups = {
      today: [],
      yesterday: [],
      week: [],
      older: []
    };

    chats.forEach(chat => {
      const chatDate = new Date(chat.updated_at || chat.created_at);
      if (chatDate >= today) {
        groups.today.push(chat);
      } else if (chatDate >= yesterday) {
        groups.yesterday.push(chat);
      } else if (chatDate >= weekAgo) {
        groups.week.push(chat);
      } else {
        groups.older.push(chat);
      }
    });

    return groups;
  };

  const groupedChats = groupChatsByDate(filteredChats);

  const handleSearchChange = (value) => {
    setSearchQuery(value);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const handleNewChat = () => {
    onCreateNewChat();
    onClose();
  };

  const handleChatClick = (chatId) => {
    onChatSelect(chatId);
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const chatDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    if (chatDate.getTime() === today.getTime()) {
      return 'Today';
    }
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    if (chatDate.getTime() === yesterday.getTime()) {
      return 'Yesterday';
    }
    return formatDistanceToNow(date, { addSuffix: true });
  };

  const getMessageSnippet = (chat, query) => {
    if (!chat.messages || !Array.isArray(chat.messages) || chat.messages.length === 0) {
      return '';
    }
    
    // Find message that contains the query
    const queryLower = query.toLowerCase();
    const matchingMessage = chat.messages.find(msg => 
      msg.content?.toLowerCase().includes(queryLower)
    );
    
    if (matchingMessage) {
      const content = matchingMessage.content || '';
      const index = content.toLowerCase().indexOf(queryLower);
      if (index !== -1) {
        const start = Math.max(0, index - 30);
        const end = Math.min(content.length, index + query.length + 30);
        let snippet = content.substring(start, end);
        if (start > 0) snippet = '...' + snippet;
        if (end < content.length) snippet = snippet + '...';
        
        // Highlight the query in the snippet
        const queryIndex = snippet.toLowerCase().indexOf(queryLower);
        if (queryIndex !== -1) {
          const before = snippet.substring(0, queryIndex);
          const match = snippet.substring(queryIndex, queryIndex + query.length);
          const after = snippet.substring(queryIndex + query.length);
          return (
            <>
              {before}
              <span className="font-semibold bg-yellow-100">{match}</span>
              {after}
            </>
          );
        }
        return snippet;
      }
    }
    
    // Fallback: return first message snippet
    const firstMessage = chat.messages[0];
    if (firstMessage?.content) {
      return firstMessage.content.length > 100 
        ? firstMessage.content.substring(0, 100) + '...'
        : firstMessage.content;
    }
    return '';
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[200] flex items-start justify-center pt-[10vh]"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.2 }}
          className="bg-white rounded-xl shadow-2xl w-full max-w-3xl mx-auto max-h-[75vh] flex flex-col overflow-hidden"
          onClick={(e) => e.stopPropagation()}
          style={{ marginLeft: 'auto', marginRight: 'auto' }}
        >
          {/* Header with Search */}
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <input
                  ref={inputRef}
                  type="text"
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  placeholder="Search chats..."
                  className="w-full pl-12 pr-12 py-3 text-base border-2 border-purple-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-purple-500 bg-white"
                />
                {searchQuery && (
                  <button
                    onClick={() => handleSearchChange('')}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    <X size={18} />
                  </button>
                )}
              </div>
            </div>

            {/* New Chat Button */}
            <button
              onClick={handleNewChat}
              className="w-full flex items-center gap-2 px-4 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
            >
              <Plus size={18} />
              <span>New chat</span>
            </button>
          </div>

          {/* Chat List */}
          <div className="flex-1 overflow-y-auto px-6 pb-6">
            {loadingMessages ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">Loading...</p>
              </div>
            ) : filteredChats.length === 0 ? (
              <div className="text-center py-8 text-gray-400">
                <p className="text-sm">
                  {searchQuery ? 'No chats found' : 'No chats'}
                </p>
              </div>
            ) : (
              <div className="space-y-1">
                {/* Today */}
                {groupedChats.today.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2">
                      TODAY
                    </div>
                    {groupedChats.today.map(chat => (
                      <ChatItem
                        key={chat.id}
                        chat={chat}
                        isSelected={currentChatId === chat.id}
                        onClick={() => handleChatClick(chat.id)}
                        searchQuery={searchQuery}
                        getMessageSnippet={getMessageSnippet}
                      />
                    ))}
                  </div>
                )}

                {/* Yesterday */}
                {groupedChats.yesterday.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2 mt-6">
                      YESTERDAY
                    </div>
                    {groupedChats.yesterday.map(chat => (
                      <ChatItem
                        key={chat.id}
                        chat={chat}
                        isSelected={currentChatId === chat.id}
                        onClick={() => handleChatClick(chat.id)}
                        searchQuery={searchQuery}
                        getMessageSnippet={getMessageSnippet}
                      />
                    ))}
                  </div>
                )}

                {/* Previous 7 Days */}
                {groupedChats.week.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2 mt-6">
                      PREVIOUS 7 DAYS
                    </div>
                    {groupedChats.week.map(chat => (
                      <ChatItem
                        key={chat.id}
                        chat={chat}
                        isSelected={currentChatId === chat.id}
                        onClick={() => handleChatClick(chat.id)}
                        searchQuery={searchQuery}
                        getMessageSnippet={getMessageSnippet}
                      />
                    ))}
                  </div>
                )}

                {/* Older */}
                {groupedChats.older.length > 0 && (
                  <div>
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 px-2 mt-6">
                      OLDER
                    </div>
                    {groupedChats.older.map(chat => (
                      <ChatItem
                        key={chat.id}
                        chat={chat}
                        isSelected={currentChatId === chat.id}
                        onClick={() => handleChatClick(chat.id)}
                        searchQuery={searchQuery}
                        getMessageSnippet={getMessageSnippet}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// Chat Item Component
function ChatItem({ chat, isSelected, onClick, searchQuery, getMessageSnippet }) {
  const snippet = searchQuery ? getMessageSnippet(chat, searchQuery) : '';

  return (
    <div
      onClick={onClick}
      className={`px-3 py-2.5 rounded-lg cursor-pointer transition-colors mb-1 ${
        isSelected
          ? 'bg-purple-50 border border-purple-200'
          : 'hover:bg-gray-50'
      }`}
    >
      <div className="flex items-start gap-3">
        <MessageSquare 
          size={18} 
          className={`mt-0.5 flex-shrink-0 ${
            isSelected ? 'text-purple-600' : 'text-gray-400'
          }`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between mb-1">
            <p className={`text-sm font-medium truncate ${
              isSelected ? 'text-purple-900' : 'text-gray-900'
            }`}>
              {chat.title || 'Untitled Chat'}
            </p>
          </div>
          {snippet && (
            <p className="text-xs text-gray-500 line-clamp-2 mt-1">
              {snippet}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

